import React, { useState } from 'react';
import Faculties from './Faculties';
import FacultyMembers from './FacultyMembers';
import Courses from './Courses';
import Services from './Services';
import Administrations from './Administrations';
import LiveLogs from './LiveLogs';
import ExportData from './ExportData';
import Holidays from './Holidays';

import './Dashboard.css';

const Dashboard = ({ user, onLogout }) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [activeMenu, setActiveMenu] = useState('faculties');

  const handleLogout = () => {
    localStorage.removeItem('pstu_diary_token');
    localStorage.removeItem('pstu_diary_user');
    onLogout();
  };

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const handleMenuClick = (menu) => {
    setActiveMenu(menu);
  };

  const menuTitles = {
    faculties: 'Faculties',
    'faculty-members': 'Faculty Members',
    courses: 'Courses',
    services: 'Services',
    administrations: 'Administrations',
    'holiday-calendar': 'Holiday Calendar',
    'export-data': 'Export Data',
    'server-logs': 'Server Logs',
    'admin-staffs': 'Admin Staffs'
  };

  const menuDescriptions = {
    faculties: 'Arrange faculties, update department structures, and keep the academic hierarchy clean.',
    'faculty-members': 'Search, edit, and review teacher profiles with faster day-to-day controls.',
    courses: 'Maintain course records, filters, and semester data in one place.',
    services: 'Manage halls and service units with drag-and-drop ordering that reflects instantly.',
    administrations: 'Organize administrative offices, subunits, and staff priorities.',
    'holiday-calendar': 'Manage class and office holidays. Add single-day or date-range holidays for any year.',
    'export-data': 'Download structured exports in JSON or CSV whenever you need a backup or handoff.',
    'server-logs': 'Monitor live server activity and download logs when debugging.',
    'admin-staffs': 'Manage administration staff records and assignments.'
  };

  return (
    <div className="dashboard">
      <header className="dashboard-header">
        <div className="header-left">
          <button className="sidebar-toggle" onClick={toggleSidebar}>
            <span className="hamburger">☰</span>
          </button>
          <div className="brand-block">
            <span className="brand-kicker">Patuakhali Science and Technology University</span>
            <h1>PSTU Diary Admin</h1>
          </div>
        </div>
        <div className="header-right">
          <div className="header-status">
            <span className="status-dot"></span>
            System online
          </div>
          <div className="user-info">
            <span className="user-name">{user?.name || 'User'}</span>
            <span className="user-email">{user?.email || 'user@example.com'}</span>
          </div>
          <button onClick={handleLogout} className="logout-button">
            Logout
          </button>
        </div>
      </header>
      
      <div className="dashboard-container">
        <aside className={`sidebar ${isSidebarOpen ? 'open' : 'closed'}`}>
          <div className="sidebar-brand">
            <div className="sidebar-brand-mark">PD</div>
            <div className="sidebar-brand-copy">
              <strong>Admin Workspace</strong>
              <span>Manage faculties, people, and records</span>
            </div>
          </div>
          <nav className="sidebar-nav">
            <ul className="nav-menu">
              <li className={`nav-item ${activeMenu === 'faculties' ? 'active' : ''}`}>
                <button onClick={() => handleMenuClick('faculties')} className="nav-link">
                  <span className="nav-icon">🏛️</span>
                  Faculties
                </button>
              </li>
              <li className={`nav-item ${activeMenu === 'faculty-members' ? 'active' : ''}`}>
                <button onClick={() => handleMenuClick('faculty-members')} className="nav-link">
                  <span className="nav-icon">👥</span>
                  All Faculty Members
                </button>
              </li>
              <li className={`nav-item ${activeMenu === 'courses' ? 'active' : ''}`}>
                <button onClick={() => handleMenuClick('courses')} className="nav-link">
                  <span className="nav-icon">📚</span>
                  All Courses
                </button>
              </li>
              <li className={`nav-item ${activeMenu === 'services' ? 'active' : ''}`}>
                <button onClick={() => handleMenuClick('services')} className="nav-link">
                  <span className="nav-icon">🔧</span>
                  All Services
                </button>
              </li>
              <li className={`nav-item ${activeMenu === 'administrations' ? 'active' : ''}`}>
                <button onClick={() => handleMenuClick('administrations')} className="nav-link">
                  <span className="nav-icon">⚙️</span>
                  Administrations
                </button>
              </li>
              <li className={`nav-item ${activeMenu === 'holiday-calendar' ? 'active' : ''}`}>
                <button onClick={() => handleMenuClick('holiday-calendar')} className="nav-link">
                  <span className="nav-icon">📅</span>
                  Holiday Calendar
                </button>
              </li>
              <li className={`nav-item ${activeMenu === 'export-data' ? 'active' : ''}`}>
                <button onClick={() => handleMenuClick('export-data')} className="nav-link">
                  <span className="nav-icon">📊</span>
                  Export Data
                </button>
              </li>
              <li className={`nav-item ${activeMenu === 'server-logs' ? 'active' : ''}`}>
                <button onClick={() => handleMenuClick('server-logs')} className="nav-link">
                  <span className="nav-icon">📜</span>
                  Server Logs
                </button>
              </li>

              {/* <li className={`nav-item ${activeMenu === 'admin-staffs' ? 'active' : ''}`}>
                <button onClick={() => handleMenuClick('admin-staffs')} className="nav-link">
                  <span className="nav-icon">👨‍💼</span>
                  Admin Staffs
                </button>
              </li> */}
            </ul>
          </nav>
        </aside>
        {isSidebarOpen && (
          <button
            type="button"
            className="sidebar-overlay"
            onClick={() => setIsSidebarOpen(false)}
            aria-label="Close navigation"
          />
        )}
        
        <main className={`dashboard-main ${isSidebarOpen ? 'sidebar-open' : 'sidebar-closed'}`}>
          <div className="page-intro">
            <div className="page-intro-copy">
              <h2>{menuTitles[activeMenu] || 'Dashboard'}</h2>
              <p>{menuDescriptions[activeMenu] || 'Manage your admin workspace from one place.'}</p>
            </div>
          </div>
          
          <div className="dashboard-content">
            {activeMenu === 'faculties' && <Faculties />}
            {activeMenu === 'faculty-members' && <FacultyMembers />}
            {activeMenu === 'courses' && <Courses />}
            {activeMenu === 'services' && <Services />}
            {activeMenu === 'administrations' && <Administrations />}
            {activeMenu === 'holiday-calendar' && <Holidays />}
            {activeMenu === 'export-data' && <ExportData />}
            {activeMenu === 'server-logs' && <LiveLogs />}

            {activeMenu === 'admin-staffs' && <div className="coming-soon">Admin Staffs Management - Coming Soon</div>}
          </div>

          {activeMenu !== 'export-data' && activeMenu !== 'server-logs' && (
            <div className="workspace-tools">
              <div className="workspace-tools-copy">
                <span className="workspace-tools-kicker">Utilities</span>
                <h3>Exports and diagnostics</h3>
                <p>Keep data export and server diagnostics close by without taking over the top of every page.</p>
              </div>
              <div className="quick-actions workspace-actions">
                <button 
                  onClick={() => handleMenuClick('export-data')}
                  className="quick-action-btn primary"
                >
                  <span className="quick-action-icon">⇩</span>
                  Open Export Center
                </button>
                <button 
                  onClick={() => handleMenuClick('server-logs')}
                  className="quick-action-btn secondary"
                >
                  <span className="quick-action-icon">≣</span>
                  View Server Logs
                </button>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default Dashboard;
